import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Bookmark, Save, MoreHorizontal, Star, Pencil, Trash2, Check } from "lucide-react";

export function SavedViews({ views, activeViewId, onSelect, onSave, onUpdate, onRename, onDelete, onSetDefault }) {
  const [dlg, setDlg] = useState(null); // {mode:"save"|"rename", name, isDefault, id}
  const active = views.find((v) => v.view_id === activeViewId);

  const openSave = () => setDlg({ mode: "save", name: "", isDefault: false });
  const openRename = () => setDlg({ mode: "rename", name: active?.name || "", id: active?.view_id });

  const submit = () => {
    const name = (dlg.name || "").trim();
    if (!name) return;
    if (dlg.mode === "save") onSave({ name, is_default: dlg.isDefault });
    else onRename(dlg.id, name);
    setDlg(null);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="saved-views">
      <Bookmark className="h-4 w-4 text-slate-500" />
      <Select value={activeViewId || "none"} onValueChange={(v) => onSelect(v === "none" ? null : v)}>
        <SelectTrigger className="h-9 w-[200px] bg-white border-slate-200 text-sm" data-testid="saved-views-select">
          <SelectValue placeholder="Saved views" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No saved view</SelectItem>
          {views.map((v) => (
            <SelectItem key={v.view_id} value={v.view_id}>
              {v.name}{v.is_default ? " ★" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button variant="outline" className="h-9 border-slate-300" onClick={openSave} data-testid="saved-views-save-btn">
        <Save className="h-4 w-4 mr-1.5" />Save view
      </Button>

      {active && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-9 px-2" data-testid="saved-views-menu-btn"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onUpdate(active.view_id)} data-testid="saved-view-update">
              <Check className="h-4 w-4 mr-2" />Update with current
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openRename} data-testid="saved-view-rename"><Pencil className="h-4 w-4 mr-2" />Rename</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSetDefault(active.view_id, !active.is_default)} data-testid="saved-view-default">
              <Star className={`h-4 w-4 mr-2 ${active.is_default ? "fill-amber-400 text-amber-400" : ""}`} />
              {active.is_default ? "Remove as default" : "Set as default"}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDelete(active.view_id)} className="text-red-600 focus:text-red-600" data-testid="saved-view-delete">
              <Trash2 className="h-4 w-4 mr-2" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="saved-view-dialog">
          <DialogHeader><DialogTitle>{dlg?.mode === "rename" ? "Rename view" : "Save dashboard view"}</DialogTitle>
            <DialogDescription>Name this configuration of filters and scope. Saved views are private to you.</DialogDescription>
          </DialogHeader>
          <Input autoFocus placeholder="View name (e.g. My Portfolio, Chanel Projects)" value={dlg?.name || ""}
            onChange={(e) => setDlg((d) => ({ ...d, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="saved-view-name-input" />
          {dlg?.mode === "save" && (
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <Checkbox checked={dlg?.isDefault} onCheckedChange={(c) => setDlg((d) => ({ ...d, isDefault: !!c }))} data-testid="saved-view-default-checkbox" />
              Set as my default dashboard
            </label>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlg(null)}>Cancel</Button>
            <Button className="bg-[#2563EB] hover:bg-[#1d4ed8]" onClick={submit} data-testid="saved-view-confirm-btn">
              {dlg?.mode === "rename" ? "Rename" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
